import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { isAfisCyprus } from 'shared-pages/common/constants';
import { FormatDate } from 'shared-pages/common/date-helpfunc';
import { ContainersOrderStates } from 'shared-pages/shared/constants';
import { BadgeStatusJudge } from '../BadgeStatusJudge';
import { Bullet } from 'shared-pages/components';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateContainer] = useMutation(gqlUpdate);
  const [addNewContainer] = useMutation(gqlAdd);
  const [deleteContainer] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('ContainerOrder'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'orderNo',
        header: t('Αρ. εντολής'),
        columnName: t('Αρ. εντολής'),
        accessorKey: 'orderNo',
        orderByField: 'orderNo',
        cell: TableCellV8,
        size: 15,
      },
      ...(!isGridInForm
        ? [
            {
              id: 'collectionpoint.name',
              header: t('Σημείο Συλλογής'),
              columnName: t('Σημείο Συλλογής'),
              accessorKey: 'collectionpoint.name',
              orderByField: 'collectionpoint.name',
              cell: TableCellV8,
              size: 15,
              meta: { type: 'text' },
            },
            {
              id: 'collectionpoint.addressLine1',
              header: t('Διεύθυνση'),
              columnName: t('Διεύθυνση'),
              accessorKey: 'collectionpoint.addressLine1',
              orderByField: 'collectionpoint.addressLine1',
              cell: ({ row }) => {
                return (
                  <>
                    {row.original?.collectionpoint?.addressLine1}
                    <br />

                    {row.original?.collectionpoint?.addressArea}
                    <br />
                    {row.original?.collectionpoint?.addressCity}
                  </>
                );
              },
              size: 15,
              meta: { type: 'text' },
            },
          ]
        : []),
      {
        id: 'dateOrdered',
        header: t('Ημ.Παραγγελίας'),
        columnName: t('Ημ.Παραγγελίας'),
        accessorKey: 'dateOrdered',
        orderByField: 'dateOrdered',
        cell: CellDateV8,
        size: 15,
        meta: { type: 'date' },
      },
      {
        id: 'containerTypeId',
        header: t('Τύπος Παραγγελίας'),
        columnName: t('Τύπος Παραγγελίας'),
        accessorKey: 'containerTypeId',
        orderByField: 'containerordertype.name',
        cell: ({ row }) => {
          return <>{row.original?.containerordertype?.name}</>;
        },
        size: 10,
      },
      {
        id: 'collectorId',
        header: t('Συλλέκτης'),
        columnName: t('Συλλέκτης'),
        accessorKey: 'collector.name',
        orderByField: 'collector.name',
        cell: TableCellV8,
        size: 15,
      },

      {
        id: 'isApproved',
        header: t('Αποδοχή'),
        columnName: t('Αποδοχή'),
        accessorKey: 'isApproved',
        orderByField: 'isApproved',
        cell: ({ row }) => {
          const rec = row.original;
          return (
            <>
              <BadgeStatusJudge statusId={rec.statusId} isApproved={rec.isApproved} />
              {rec.isApproved && (
                <div className="text-center">
                  <span>{FormatDate(rec.judgeDate)}</span>
                  <span> {rec.judgedByUser}</span>
                </div>
              )}
              {!rec.isApproved && (
                <div className="text-center">
                  <span>{FormatDate(rec.judgeDate)}</span>
                  <span> {rec.judgedNotes}</span>
                  <span> {rec.judgedByUser}</span>
                </div>
              )}
            </>
          );
        },
        size: 10,
      },

      {
        id: 'lines',
        header: t('Γραμμές Παραγγελίας'),
        columnName: t('Γραμμές Παραγγελίας'),
        accessorKey: 'lines', // from the resolver's include: { as: 'lines' }
        cell: ({ row }) => {
          const lines = row.original?.lines ?? [];
          if (!lines.length) return <>{t('No lines')}</>;

          return (
            <div style={{ lineHeight: 1.5 }}>
              {lines.map((ln, idx, array) => {
                const prefix = array?.length > 1 ? <>{idx + 1}: </> : null;
                return (
                  <div
                    key={ln.id || idx}
                    className={parseFloat(ln.quantity) < 0 ? 'text-danger' : ''}
                  >
                    {prefix}
                    {ln.containertype?.name ?? t('Άγνωστο είδος')} <Bullet /> {ln.quantity}{' '}
                    {t('τμχ')}
                    {isAfisCyprus ? '  [' + ln.binSerialNo + ']' : ''}
                  </div>
                );
              })}
            </div>
          );
        },
        size: 20,
      },
    ],
    [t],
  );
  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
            };
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Containers - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addContainertype.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τον Κάδο <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteContainer({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Κάδος');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
